import {combineReducers} from 'redux'
import {createPivotTableReducer} from 'reducers/pivotTable'
import Immutable from "seamless-immutable"
import {get, isEmpty, isNil} from 'lodash'
import types from 'actions/types'

const result = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RUN_SUCCESS:
      return action.result
    case types.PRICING_CALCULATOR_RUN_FAILURE:
    case types.PRICING_CALCULATOR_RESULT_RESET:
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return null
    default:
      return state
  }
}

export const isCalculating = (state = false, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RUN_ATTEMPT:
      return true
    case types.PRICING_CALCULATOR_RUN_SUCCESS:
    case types.PRICING_CALCULATOR_RUN_FAILURE:
    case types.PRICING_CALCULATOR_RESULT_RESET:
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return false
    default:
      return state
  }
}

const error = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RUN_FAILURE:
      return {message: action.message, code: action.code}
    case types.PRICING_CALCULATOR_RUN_ATTEMPT:
    case types.PRICING_CALCULATOR_RUN_SUCCESS:
    case types.PRICING_CALCULATOR_RESULT_RESET:
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return null
    default:
      return state
  }
}

export const lastExecution = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_LAST_EXECUTION_SET:
      return Immutable({calculator: action.calculator, version: action.version, request: action.request})
    case types.PRICING_CALCULATOR_FORM_CLEAR:
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return null
    default:
      return state
  }
}

export const isSendingToSupport = (state = false, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_SEND_TO_SUPPORT_ATTEMPT:
      return true
    case types.PRICING_CALCULATOR_SEND_TO_SUPPORT_SUCCESS:
    case types.PRICING_CALCULATOR_SEND_TO_SUPPORT_FAILURE:
      return false
    default:
      return state
  }
}

const partPricing = (state = {}, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_PART_PRICING_SET:
      return {...state, [action.partId]: action.pricing}
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return {}
    default:
      return state
  }
}

const fieldMappings = (state = {}, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_FIELD_MAPPINGS_SET:
      return {...state, [action.calculatorKey]: action.mappings}
    default:
      return state
  }
}

export const calculatorInstance = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_FETCH_SUCCESS:
      return Immutable(action.calculatorInstance)
    case types.PRICING_CALCULATOR_FORM_CLEAR:
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return null
    default:
      return state
  }
}

export const formDefinition = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_FORM_FETCH_SUCCESS:
      return Immutable(action.formDefinition)
    case types.PRICING_CALCULATOR_FORM_CLEAR:
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return null
    default:
      return state
  }
}

export const formLoading = (state = false, action) => {
  switch (action.type) {
    case types.QUOTING_QUOTE_SHOW_PRICING_CALCULATOR_DRAWER:
    case types.PRICING_CALCULATOR_FORM_FETCH_ATTEMPT:
      return true
    case types.PRICING_CALCULATOR_FORM_FETCH_SUCCESS:
    case types.PRICING_CALCULATOR_FORM_FETCH_FAILURE:
      return false
    default:
      return state
  }
}

export const formError = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_FORM_FETCH_FAILURE:
      return action.message
    case types.QUOTING_QUOTE_SHOW_PRICING_CALCULATOR_DRAWER:
    case types.PRICING_CALCULATOR_FORM_FETCH_ATTEMPT:
    case types.PRICING_CALCULATOR_FORM_FETCH_SUCCESS:
    case types.PRICING_CALCULATOR_FORM_CLEAR:
    case types.PRICING_CALCULATOR_RUN_CLEAR:
      return null
    default:
      return state
  }
}

export const resourceTypes = (state = [], action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RESOURCES_FORM_FETCH_SUCCESS:
      return Immutable(action.resourceTypes)
    default:
      return state
  }
}

export const resources = (state = [], action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RESOURCES_LOAD_SUCCESS:
      return Immutable(action.resources)
    default:
      return state
  }
}

export const isResourcesFormLoading = (state = false, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RESOURCES_FORM_FETCH_ATTEMPT:
      return true
    case types.PRICING_CALCULATOR_RESOURCES_FORM_FETCH_SUCCESS:
    case types.PRICING_CALCULATOR_RESOURCES_FORM_FETCH_FAILURE:
      return false
    default:
      return state
  }
}

const resourceTablesKey = 'pricingCalculator.resourceTables'
export const resourceTableKey = (typeName) => `${resourceTablesKey}.${typeName}`

export const resourceModesTableKey = 'pricingCalculator.resourceModesTable'

const KEY_PREFIX = `${resourceTablesKey}.`
const typeFromKey = (key) => {
  if (typeof key !== 'string' || !key.startsWith(KEY_PREFIX)) return null

  const typeName = key.slice(KEY_PREFIX.length)
  return isEmpty(typeName) ? null : typeName
}

const pivotReducers = {}
const pivotReducerFor = (typeName) => {
  if (isNil(pivotReducers[typeName])) {
    pivotReducers[typeName] = createPivotTableReducer({
      key: resourceTableKey(typeName),
      defaultSortColumn: 'name',
    })
  }
  return pivotReducers[typeName]
}

// Defaults go underneath: persistence restores only the whitelisted keys.
const withDefaults = (typeName, existing, action) => ({
  ...pivotReducerFor(typeName)(undefined, action),
  ...(existing || {}),
})

export const resourceTables = (state = {}, action) => {
  if (action.type === types.STATE_LOAD_SUCCESS || action.type === types.CONFIG_LOAD_SUCCESS) {
    return Object.keys(state).reduce((next, typeName) => {
      next[typeName] = withDefaults(typeName, state[typeName], action)
      return next
    }, {})
  }

  if (action.type === types.PRICING_CALCULATOR_RESOURCES_FORM_FETCH_SUCCESS) {
    return (action.resourceTypes || []).reduce((next, resourceType) => {
      const typeName = get(resourceType, 'name')
      if (typeName) {
        next[typeName] = withDefaults(typeName, next[typeName], action)
      }
      return next
    }, {...state})
  }

  const typeName = typeFromKey(action.key)
  if (isNil(typeName)) return state

  const current = state[typeName]
  const next = pivotReducerFor(typeName)(current, action)
  return next === current ? state : {...state, [typeName]: next}
}

export const includeDormantResources = (state = {}, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RESOURCE_INCLUDE_DORMANT_TOGGLE:
      return {...state, [action.resourceType]: !!action.includeDormant}
    default:
      return state
  }
}

export const selectedResourceType = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RESOURCE_TYPE_SELECT:
      return action.resourceType
    default:
      return state
  }
}

export const isSavingResource = (state = false, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RESOURCE_ADD_ATTEMPT:
    case types.PRICING_CALCULATOR_RESOURCE_UPDATE_ATTEMPT:
      return true
    case types.PRICING_CALCULATOR_RESOURCE_ADD_SUCCESS:
    case types.PRICING_CALCULATOR_RESOURCE_ADD_FAILURE:
    case types.PRICING_CALCULATOR_RESOURCE_UPDATE_SUCCESS:
    case types.PRICING_CALCULATOR_RESOURCE_UPDATE_FAILURE:
      return false
    default:
      return state
  }
}

export const resourceSaveError = (state = null, action) => {
  switch (action.type) {
    case types.PRICING_CALCULATOR_RESOURCE_ADD_FAILURE:
    case types.PRICING_CALCULATOR_RESOURCE_UPDATE_FAILURE:
      return action.message
    case types.PRICING_CALCULATOR_RESOURCE_ADD_ATTEMPT:
    case types.PRICING_CALCULATOR_RESOURCE_ADD_SUCCESS:
    case types.PRICING_CALCULATOR_RESOURCE_UPDATE_ATTEMPT:
    case types.PRICING_CALCULATOR_RESOURCE_UPDATE_SUCCESS:
      return null
    default:
      return state
  }
}

export default combineReducers({
  result,
  isCalculating,
  error,
  lastExecution,
  isSendingToSupport,
  partPricing,
  fieldMappings,
  calculatorInstance,
  formDefinition,
  formLoading,
  formError,
  resourceTypes,
  resources,
  isResourcesFormLoading,
  resourceTables,
  resourceModesTable: createPivotTableReducer({key: resourceModesTableKey, defaultSortColumn: null}),
  selectedResourceType,
  includeDormantResources,
  isSavingResource,
  resourceSaveError,
})
